/**
 * LA HORA DE ESPAÑA EN EL FRONT (doc del 16-08, regla 1: «Todo en hora de España. Nada en
 * UTC»).
 *
 * El backend ya tiene su `core/tiempo.py`; esto es el mismo criterio del lado del
 * navegador. Importa porque lo que se pinta no es «una hora»: es «ayer, 21:40» y «te
 * quedan 2 días», y las dos cosas cambian de valor según dónde esté el reloj de quien
 * mira. Fiarse de la zona del aparato funcionaba mientras todos los clientes estaban en
 * España; el plazo de un reporte se anuncia «a las 18:00 h España» y tiene que decir lo
 * mismo desde Canarias o desde Dubái.
 *
 * Las fechas llegan de Mongo en ISO UTC. Las viejas (importadas) a veces vienen sin zona:
 * se asumen UTC, que es como escribe todo el backend.
 */
const ZONA = 'Europe/Madrid';

const partes = (fecha, opciones) => Object.fromEntries(
    new Intl.DateTimeFormat('es-ES', { timeZone: ZONA, ...opciones })
        .formatToParts(fecha)
        .map((p) => [p.type, p.value]),
);

/** Un ISO (o Date) a Date. Devuelve null si no se entiende: quien pinta ya sabe vivir sin ello. */
export function aFecha(valor) {
    if (!valor) return null;
    if (valor instanceof Date) return Number.isNaN(valor.getTime()) ? null : valor;
    let texto = String(valor).trim().replace(' ', 'T');
    if (!/(Z|[+-]\d{2}:?\d{2})$/.test(texto)) texto += 'Z';
    const d = new Date(texto);
    return Number.isNaN(d.getTime()) ? null : d;
}

/** El día de esa fecha PARA EL CLIENTE: 'YYYY-MM-DD' en hora de España. */
export function diaEnEspana(fecha) {
    const p = partes(fecha, { year: 'numeric', month: '2-digit', day: '2-digit' });
    return `${p.year}-${p.month}-${p.day}`;
}

/** '21:40' en hora de España. */
export function horaEnEspana(fecha) {
    const p = partes(fecha, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    return `${p.hour}:${p.minute}`;
}

/**
 * EL RELOJ DE LA APP: lo que decide un día o un plazo usa `ahoraDeLaApp()` en vez de
 * `new Date()`, como el servidor usa `core/tiempo.ahora_utc`. Lo que mide un rato (cuánto
 * tardó algo, un temporizador) sigue con `Date.now()`.
 */
let minutosPorDelante = 0;

/** LA FECHA DE UNA CUENTA DE PRUEBA (0.2 del encargo de Jesús, 02-11): el rato que va por
 * delante, el mismo que aplica el servidor (`core/fecha_de_prueba`). Lo deja aquí el usuario
 * (`minutos_por_delante`, en `AuthContext`). Una cuenta normal va a 0. */
export function fijarMinutosPorDelante(minutos) {
    const n = Number(minutos);
    minutosPorDelante = Number.isInteger(n) && n > 0 ? n : 0;
}

export const ahoraDeLaApp = () => new Date(Date.now() + minutosPorDelante * 60 * 1000);

/** «3 d 5 h», «45 min»: cuánto va por delante una cuenta de prueba. */
export function ratoCorto(minutos) {
    const d = Math.floor(minutos / (24 * 60));
    const h = Math.floor((minutos % (24 * 60)) / 60);
    const m = minutos % 60;
    return [d && `${d} d`, h && `${h} h`, m && `${m} min`].filter(Boolean).join(' ') || '0 min';
}

export const hoyEnEspana = () => diaEnEspana(ahoraDeLaApp());

/**
 * UN DÍA ES UN DÍA: NO SE CONVIERTE DE HUSO (10-10).
 *
 * Las fechas que son un día y no un instante (inicio del ciclo, próximo cobro, renovación,
 * fecha de un pago apuntado) se guardan como 'AAAA-MM-DD' o como ese día a las 00:00 UTC
 * («2026-10-19T00:00:00+00:00»). Pintadas con `new Date(valor).toLocaleDateString()`, en un
 * navegador por detrás de Greenwich salían un día antes: la ficha decía «Inicio del ciclo
 * 18/10/2026», un domingo, de un ciclo que arranca el lunes 19.
 *
 * `diaDeCalendario` devuelve ese día tal cual, 'AAAA-MM-DD'. Si lo que llega es un instante
 * de verdad (con su hora: un alta, un cobro de Stripe), el día es el de España, que es donde
 * corren los plazos del negocio y el mismo para quien mire desde donde mire.
 */
export function diaDeCalendario(valor) {
    if (!valor) return null;
    if (!(valor instanceof Date)) {
        const texto = String(valor).trim();
        const m = /^(\d{4}-\d{2}-\d{2})(?:$|[T ]00:00(?::00(?:\.0+)?)?(?:Z|[+-]00:?00)?$)/.exec(texto);
        if (m) return m[1];
    }
    const d = aFecha(valor);
    return d ? diaEnEspana(d) : null;
}

/** Ese día, escrito: `pintarDia('2026-10-19T00:00:00+00:00')` -> «19/10/2026» en cualquier
 *  huso. `opciones` son las de `toLocaleDateString`. Si no se entiende, `vacio`. */
export function pintarDia(valor, opciones, vacio = '') {
    const dia = diaDeCalendario(valor);
    if (!dia) return vacio;
    const [a, m, d] = dia.split('-').map(Number);
    return new Date(Date.UTC(a, m - 1, d, 12)).toLocaleDateString('es-ES', { ...opciones, timeZone: 'UTC' });
}

/**
 * EL DÍA DEL RELOJ DEL CLIENTE, 'YYYY-MM-DD' (bloque F, 23-08).
 *
 * La regla de producto que faltaba: el «hoy» que el cliente VE Y EDITA -- el saludo, el
 * día que se abre, marcar comidas, el cierre, el entreno -- es el de SU reloj. La hora
 * de España queda para lo que es un plazo del negocio (ventanas de reportes, avisos),
 * que ya la usan `textoPlazo` y compañía. Un cliente en México en su sábado por la
 * noche veía «Domingo» y la app le abría un día que aún no había vivido.
 *
 * `en-CA` da YYYY-MM-DD en la zona del navegador. OJO: `toISOString().slice(0,10)` NO
 * vale para esto -- es el día UTC -- y es la trampa que había repartida por la app.
 */
export const hoyLocal = () => ahoraDeLaApp().toLocaleDateString('en-CA');

/**
 * El lunes que viene a partir de un día 'AAAA-MM-DD' (por defecto, el hoy del reloj local).
 * Si ese día ya es lunes, el de la semana siguiente: «el lunes que viene» nunca es hoy.
 * Se cuenta sobre el día escrito, a mediodía UTC, para que ningún huso lo mueva.
 * Es la fecha por defecto de la próxima versión de la suplementación (20-10).
 */
export function lunesSiguiente(dia = hoyLocal()) {
    if (!esUnDia(dia)) return null;
    const [a, m, d] = dia.split('-').map(Number);
    const f = new Date(Date.UTC(a, m - 1, d, 12));
    const hastaElLunes = ((8 - f.getUTCDay()) % 7) || 7;
    f.setUTCDate(f.getUTCDate() + hastaElLunes);
    const dos = (n) => String(n).padStart(2, '0');
    return `${f.getUTCFullYear()}-${dos(f.getUTCMonth() + 1)}-${dos(f.getUTCDate())}`;
}

/**
 * ¿Es 'AAAA-MM-DD' Y ADEMÁS un día que existe? (MAC-10, 14-10)
 *
 * Con mirar la forma no basta: «2026-13-45» y «2026-02-30» la cumplen. El primero tumbaba el
 * calendario de Nutrición y el segundo se pintaba como el 2 de marzo, porque `Date` corre los
 * días que sobran al mes siguiente en vez de quejarse. Se construye la fecha con sus tres
 * números (local, sin huso) y se comprueba que no se ha movido.
 */
export function esUnDia(texto) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(typeof texto === 'string' ? texto : '');
    if (!m) return false;
    const [anio, mes, dia] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const d = new Date(anio, mes - 1, dia);
    return d.getFullYear() === anio && d.getMonth() === mes - 1 && d.getDate() === dia;
}

/** El día LOCAL (reloj del cliente) de un instante ISO/Date: 'YYYY-MM-DD'.
 *  Un día suelto ('2026-10-14', sin hora) ya es un día y se devuelve tal cual: leído como
 *  medianoche UTC, al oeste de Greenwich salía la víspera (12-10 en el mensual, 14-10 aquí). */
export function diaLocal(valor) {
    if (typeof valor === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(valor.trim())) return valor.trim();
    const d = aFecha(valor);
    return d ? d.toLocaleDateString('en-CA') : null;
}

/** Días enteros entre ese día y el HOY DEL CLIENTE. Positivo = ya pasó. */
export function diasDesdeHoyLocal(dia) {
    return numeroDeDia(hoyLocal()) - numeroDeDia(dia);
}

const numeroDeDia = (dia) => Math.floor(Date.parse(`${dia}T00:00:00Z`) / 86400000);

/** Días enteros entre ese día y hoy, en España. Positivo = ya pasó. */
export function diasDesdeHoy(dia) {
    return numeroDeDia(hoyEnEspana()) - numeroDeDia(dia);
}

/** 'jueves, 20 de agosto' (la cabecera de Inicio; la mayúscula la pone el CSS).
 *  EL DÍA DEL CLIENTE (bloque F, 23-08): es su saludo, no un plazo. Con la zona de
 *  Madrid, un cliente en América leía «Domingo» en su sábado. */
export function fechaLargaDeHoy() {
    const p = Object.fromEntries(new Intl.DateTimeFormat('es-ES', {
        weekday: 'long', day: 'numeric', month: 'long',
    }).formatToParts(ahoraDeLaApp()).map((x) => [x.type, x.value]));
    return `${p.weekday}, ${p.day} de ${p.month}`;
}

/**
 * «ayer, 21:40». Lo que sustituye a la palabra «pendiente» en el cierre del día: la fecha
 * y la hora del último registro dicen si se lleva al día mejor que una etiqueta.
 * «hoy»/«ayer» se cuentan EN EL RELOJ DEL CLIENTE (bloque F): son sus palabras, no un
 * plazo. La hora también se enseña en la suya.
 */
export function etiquetaMomento(valor) {
    const d = aFecha(valor);
    if (!d) return '';
    const dias = diasDesdeHoyLocal(diaLocal(d));
    const q = Object.fromEntries(new Intl.DateTimeFormat('es-ES', {
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(d).map((x) => [x.type, x.value]));
    const hora = `${q.hour}:${q.minute}`;
    if (dias <= 0) return `hoy, ${hora}`;
    if (dias === 1) return `ayer, ${hora}`;
    const p = Object.fromEntries(new Intl.DateTimeFormat('es-ES', {
        day: 'numeric', month: 'long',
    }).formatToParts(d).map((x) => [x.type, x.value]));
    return `${p.day} de ${p.month}, ${hora}`;
}

/**
 * El plazo de un reporte: «Hasta el sábado 22 a las 10:00 · te queda 1 día».
 *
 * EL PLAZO SE FIJA EN ESPAÑA Y SE ENSEÑA EN LA HORA DEL CLIENTE (doc 21-08, apartado
 * 15). Si el reloj del navegador marca otra hora que el de España, se dice la suya:
 * «Hasta el sábado 22 a las 05:00». SIN «h España» EN NINGÚN CASO (Francisco, 25-10: «quita
 * lo de hora España de todos lados»): antes, fuera de España, llevaba la de España entre
 * paréntesis, «(10:00 h España)».
 *
 * La hora sale del plazo de verdad, no escrita a mano. `pasado` es lo que hace
 * desaparecer la línea de Inicio en cuanto se cierra.
 */
export function textoPlazo(valor) {
    const d = aFecha(valor);
    if (!d) return null;
    const pasado = d.getTime() <= ahoraDeLaApp().getTime();
    const p = partes(d, { weekday: 'long', day: 'numeric' });
    const enEspana = `${p.weekday} ${p.day} a las ${horaEnEspana(d)}`;
    let cuando = enEspana;
    try {
        // Sin timeZone: el huso del navegador, que es donde vive quien mira.
        const q = Object.fromEntries(new Intl.DateTimeFormat('es-ES', {
            weekday: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
            hourCycle: 'h23',
        }).formatToParts(d).map((x) => [x.type, x.value]));
        const local = `${q.weekday} ${q.day} a las ${q.hour}:${q.minute}`;
        if (local !== enEspana) cuando = local;
    } catch { /* navegador sin huso legible: se queda la hora de España */ }
    const dias = -diasDesdeHoy(diaEnEspana(d));
    const cola = dias > 1 ? ` · te quedan ${dias} días`
        : dias === 1 ? ' · te queda 1 día'
        : ' · hoy es el último día';
    return {
        pasado,
        dias,
        texto: `Hasta el ${cuando}${pasado ? '' : cola}`,
    };
}

// Las horas en punto, escritas («a las ocho», no «a las 20:00»). Es como habla él en todo
// el documento del 1-09, y en una línea de Inicio -- que se lee de pasada, no se consulta --
// una hora en dígitos obliga a traducirla. Con minutos no hay forma bonita de decirlo, así
// que ahí se queda el reloj: «a las 20:30».
const EN_PALABRAS = ['doce', 'una', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete',
    'ocho', 'nueve', 'diez', 'once'];
// LA UNA VA EN SINGULAR (12-10): con el plazo de las 18:00 de España visto desde un reloj
// donde cae a la una, la línea decía "hasta hoy a las una". Devuelve la hora con su artículo.
export const horaEnPalabras = (hhmm) => {
    const [h, m] = String(hhmm).split(':');
    if (m !== '00') return `${Number(h) === 1 ? 'la' : 'las'} ${hhmm}`;
    const n = Number(h) % 12;
    return n === 1 ? 'la una' : `las ${EN_PALABRAS[n]}`;
};

/**
 * EL MISMO PLAZO, DICHO COMO LO DICE ÉL («Todo lo validado antes del 1 de septiembre»,
 * «La cola de Inicio»): «Tienes hasta mañana jueves a las ocho», y el último día «Solo
 * recordarte que tienes hasta hoy a las ocho».
 *
 * No es un texto distinto para un aviso distinto: es ESTA MISMA LÍNEA cambiando de texto.
 * «El aviso del plazo no es un aviso aparte: así el jueves por la tarde sigue habiendo dos
 * filas, no tres.»
 *
 * Frente a `textoPlazo`, que sigue viva donde se consulta un plazo con calma («Hasta el
 * jueves 4 a las 20:00 · te queda 1 día»), aquí se habla: sin fecha cuando no hace falta
 * -- hoy y mañana ya sitúan -- y con la hora escrita. La regla de la zona es la misma y no
 * se duplica: si el reloj del cliente marca otra hora que el de España, va la suya (sin la
 * de España detrás desde el 25-10).
 */
export function plazoEnPalabras(valor) {
    const base = textoPlazo(valor);
    if (!base) return null;
    const d = aFecha(valor);
    const p = partes(d, { weekday: 'long', day: 'numeric' });
    let hora = horaEnPalabras(horaEnEspana(d));
    let diaSemana = p.weekday;
    try {
        const q = Object.fromEntries(new Intl.DateTimeFormat('es-ES', {
            weekday: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
            hourCycle: 'h23',
        }).formatToParts(d).map((x) => [x.type, x.value]));
        const local = `${q.weekday} ${q.day} ${q.hour}:${q.minute}`;
        if (local !== `${p.weekday} ${p.day} ${horaEnEspana(d)}`) {
            diaSemana = q.weekday;
            hora = horaEnPalabras(`${q.hour}:${q.minute}`);   // sin «(h España)», 25-10
        }
    } catch { /* navegador sin huso legible: se queda la hora de España */ }
    // Los días se cuentan contra el día del PLAZO, no contra el reloj: «mañana» tiene que
    // seguir siendo mañana a las 23:50.
    //
    // `hasta` es el trozo suelto -- «hasta mañana jueves a las ocho» --, que es como va en
    // la cabecera del reporte («Semana 2 · hasta mañana jueves a las ocho»). `texto` es la
    // frase entera de la línea de Inicio, que además cambia el último día.
    // `cuando` es la fecha SOLA -- «el lunes 14 a las seis» --, sin el «hasta» delante, y
    // `hasta` es esa misma con él. Hacen falta las dos: la cabecera del reporte dice
    // «Semana 3 · hasta el lunes 14 a las seis» y el aviso del paso 3 dice «tienes como
    // fecha límite el lunes 14 a las seis». Con una sola salía «fecha límite EL HASTA EL
    // lunes 14», que es lo que leía el cliente.
    const cuando = base.dias <= 0
        ? `hoy a ${hora}`
        : base.dias === 1
            ? `mañana ${diaSemana} a ${hora}`
            : `el ${diaSemana} ${p.day} a ${hora}`;
    const hasta = `hasta ${cuando}`;
    const texto = base.dias <= 0
        ? `Solo recordarte que tienes ${hasta}`
        : `Tienes ${hasta}`;
    return { ...base, hasta, cuando, texto };
}

const MESES_CORTOS = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

/** «26-OCT» o «1-ENE», el día en hora de España (columna Fecha de los avisos, 26-10).
 *  Cadena vacía si la fecha no se entiende. */
export function fechaCorta(valor) {
    const d = aFecha(valor);
    if (!d) return '';
    const [, mes, dia] = diaEnEspana(d).split('-').map(Number);
    return `${dia}-${MESES_CORTOS[mes - 1]}`;
}
